import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useProductPrefill, useChannelContent } from '@/hooks/useAmazonAudit'
import { useStartChannelAudit } from '@/hooks/useAmazonMutations'
import { getProducts } from '@/lib/api/products'
import type { Product } from '@/types/products'
import { ProductSearchInput } from './ProductSearchInput'
import { ChannelChips } from './ChannelChips'
import { MarketplaceChipSelector } from './MarketplaceChipSelector'

interface ChannelAuditTabContentProps {
  onOpenChange: (open: boolean) => void
}

export function ChannelAuditTabContent({ onOpenChange }: ChannelAuditTabContentProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [product, setProduct] = useState<Product | null>(null)
  const [channelId, setChannelId] = useState<string | null>(null)
  const [tld, setTld] = useState('de')
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<Product[]>([])
  const [showDrop, setShowDrop] = useState(false)
  const { data: prefill } = useProductPrefill(product?.id ?? null)
  const { data: content } = useChannelContent(product?.id ?? null, channelId)
  const { mutateAsync: startAudit, isPending } = useStartChannelAudit()

  useEffect(() => {
    if (!search.trim()) return
    const timer = setTimeout(async () => {
      const resp = await getProducts({ page: 1, page_size: 20, search })
      setResults(resp.products ?? [])
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Clear results when search is empty
  if (!search.trim() && results.length > 0) {
    setResults([])
  }

  const handleSelect = (p: Product) => {
    setProduct(p); setChannelId(null); setSearch(''); setShowDrop(false)
  }

  const handleStart = async () => {
    if (!product || !channelId) return
    const res = await startAudit({ product_id: product.id, channel_id: channelId, tld })
    onOpenChange(false)
    navigate(`/workspace/amazon/${res.job_id}`)
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          {t('amazonListing.channel.selectProduct')}
        </label>
        {product ? (
          <SelectedBadge name={product.name} onClear={() => { setProduct(null); setChannelId(null) }} />
        ) : (
          <ProductSearchInput search={search} setSearch={setSearch} onFocus={() => setShowDrop(true)}
            results={results} showDropdown={showDrop} onSelect={handleSelect}
            onBlur={() => setTimeout(() => setShowDrop(false), 200)} />
        )}
      </div>

      {product && prefill?.channels && prefill.channels.length > 0 && (
        <ChannelChips channels={prefill.channels} selected={channelId} onSelect={setChannelId} />
      )}
      {product && !prefill?.channels?.length && (
        <p className="text-xs text-neutral-500">{t('amazonListing.manual.noChannels')}</p>
      )}

      {content && <ContentPreview title={content.title} bullets={content.bullets.length} images={content.images.length} />}

      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          {t('amazonListing.marketplace')}
        </label>
        <MarketplaceChipSelector selected={tld} onSelect={setTld} />
      </div>

      <button onClick={handleStart} disabled={!product || !channelId || isPending}
        className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
        {isPending ? t('common.loading') : t('amazonListing.channel.startAnalysis')}
      </button>
    </div>
  )
}

function SelectedBadge({ name, onClear }: { name: string; onClear: () => void }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg text-sm">
      <span className="font-medium text-orange-700 dark:text-orange-400">{name}</span>
      <button onClick={onClear} className="ml-2 text-orange-600 hover:text-orange-800 text-xs">&times;</button>
    </div>
  )
}

function ContentPreview({ title, bullets, images }: { title: string; bullets: number; images: number }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-3 space-y-1">
      <div className="text-xs font-medium text-neutral-500">{t('amazonListing.channel.preview')}</div>
      <p className="text-sm text-neutral-900 dark:text-white truncate">{title}</p>
      <div className="flex gap-3 text-xs text-neutral-500">
        <span>{bullets} Bullets</span>
        <span>{images} {t('common.images')}</span>
      </div>
    </div>
  )
}
